import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
  useCallback
} from 'react';
import axios from 'utils/axios';
import useAuth from 'hooks/useAuth';
import { FeatureFlag, FeatureFlagId } from '../@types/featureFlags';

type FeatureFlagsContextProps = {
  flags: FeatureFlag[];
  loading: boolean;
  updateFeatureFlag: (flag: FeatureFlag) => Promise<void>;
  isEnabledForCurrentOrganization: (flagName: FeatureFlagId) => boolean;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextProps | undefined>(undefined);

type FeatureFlagsProviderProps = {
  children: ReactNode;
};

export const FeatureFlagsProvider = ({ children }: FeatureFlagsProviderProps) => {
  const { isAuthenticated, selectedOrg } = useAuth();
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);

  const updateFeatureFlag = useCallback(async (flag: FeatureFlag) => {
    await axios.put(`/feature-flags`, flag);
    setFlags((prevFlags) => prevFlags.map((f) => (f.id === flag.id ? flag : f)));
  }, []);

  const isEnabledForCurrentOrganization = useMemo(
    () => (flagName: FeatureFlagId) => {
      const flag = flags.find((f) => f.id === flagName);
      return flag ? flag.isEnabledForCurrentOrganization : false;
    },
    [flags]
  );

  useEffect(() => {
    const fetchFlags = async () => {
      if (!isAuthenticated) {
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get('/feature-flags');
        const knownFlags = (response.data as FeatureFlag[]).filter(
          (flag) => FeatureFlagId[flag.id] !== undefined
        );
        setFlags(knownFlags);
      } catch (error) {
        console.error('Failed to fetch feature flags:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFlags();
  }, [isAuthenticated, selectedOrg]);

  const contextValue = useMemo(() => {
    return {
      flags,
      loading,
      updateFeatureFlag,
      isEnabledForCurrentOrganization
    };
  }, [flags, loading, updateFeatureFlag, isEnabledForCurrentOrganization]);

  return (
    <FeatureFlagsContext.Provider value={contextValue}>{children}</FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};
